import { useEffect, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { z } from "zod";

import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { isBlank } from "../../lib/cibil/format";
import {
  fetchDirectorCibil,
  getDirectorsCibil,
  type DirectorCibilRow,
} from "../../services/cibilApi";

/** Sent verbatim as consent_text once the checkbox is ticked. */
const CONSENT_TEXT =
  "We confirm obtaining valid customer consent to access/process their mobile/pan/name data. Consent remains valid, informed, and unwithdrawn.";

const schema = z.object({
  name: z.string().trim().min(2, "Enter the director's full name").max(100),
  pan: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{5}[0-9]{4}[A-Z]$/, "Enter a valid PAN (e.g. ABCDE1234F)"),
  mobile: z
    .string()
    .trim()
    .regex(/^[6-9][0-9]{9}$/, "Enter a valid 10-digit mobile number"),
  email: z
    .string()
    .trim()
    .refine(
      (value) => value === "" || z.string().email().safeParse(value).success,
      "Enter a valid email address"
    ),
});

export interface FetchCibilInput {
  name: string;
  pan: string;
  mobile: string;
  email: string;
}

function cleanDefault(value: string | null | undefined): string {
  if (isBlank(value)) return "";
  return String(value).trim();
}

function buildFormFromDefaults(
  defaults?: Partial<FetchCibilInput>
): FetchCibilInput {
  return {
    name: cleanDefault(defaults?.name),
    pan: cleanDefault(defaults?.pan).toUpperCase(),
    mobile: cleanDefault(defaults?.mobile).replace(/\D/g, "").slice(0, 10),
    email: cleanDefault(defaults?.email),
  };
}

export function FetchCibilDialog({
  open,
  onOpenChange,
  defaults,
  customerId,
  directorsUuid,
  onFetched,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaults?: Partial<FetchCibilInput>;
  customerId?: string;
  directorsUuid?: string;
  /** Called once the pull succeeds. Passes refreshed director list when customerId is set. */
  onFetched: (row: DirectorCibilRow, list?: DirectorCibilRow[]) => void;
}) {
  const [form, setForm] = useState<FetchCibilInput>(() =>
    buildFormFromDefaults(defaults)
  );
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  // Re-populate name / PAN / mobile whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    setForm(buildFormFromDefaults(defaults));
    setConsent(false);
    setErrors({});
    setServerError(null);
    setLoading(false);
  }, [open, defaults?.name, defaults?.pan, defaults?.mobile, defaults?.email]);

  const set =
    (key: keyof FetchCibilInput) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!consent) return;
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        next[String(issue.path[0])] = issue.message;
      }
      setErrors(next);
      return;
    }
    setErrors({});
    setServerError(null);
    setLoading(true);
    try {
      const email = parsed.data.email?.trim();
      const row = await fetchDirectorCibil({
        name: parsed.data.name,
        mobile: parsed.data.mobile,
        pan: parsed.data.pan,
        consent_text: CONSENT_TEXT,
        ...(email ? { email } : {}),
        ...(customerId ? { customer_id: customerId } : {}),
        ...(directorsUuid ? { director_uuid: directorsUuid } : {}),
      });
      const list = customerId ? await getDirectorsCibil(customerId) : undefined;
      onFetched(row, list);
      onOpenChange(false);
    } catch (err) {
      const message =
        err && typeof err === "object" && "message" in err
          ? String((err as { message: unknown }).message)
          : err instanceof Error
            ? err.message
            : "Failed to fetch CIBIL report";
      setServerError(message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !loading && onOpenChange(v)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Fetch CIBIL report</DialogTitle>
          <DialogDescription>
            We will pull the director&apos;s credit bureau report using these
            details.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <p className="font-medium">Fetching bureau report…</p>
            <p className="text-xs text-gray-500">
              Contacting CIBIL. This usually takes a few seconds.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="grid gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="fc-name">
                Full name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="fc-name"
                value={form.name}
                onChange={set("name")}
                maxLength={100}
                required
              />
              {errors.name && (
                <p className="text-xs text-red-600">{errors.name}</p>
              )}
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="fc-pan">
                PAN <span className="text-red-500">*</span>
              </Label>
              <Input
                id="fc-pan"
                value={form.pan}
                onChange={(e) =>
                  setForm((f) => ({ ...f, pan: e.target.value.toUpperCase() }))
                }
                maxLength={10}
                placeholder="ABCDE1234F"
                className="uppercase"
                required
              />
              {errors.pan && (
                <p className="text-xs text-red-600">{errors.pan}</p>
              )}
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="fc-mobile">
                Mobile number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="fc-mobile"
                inputMode="numeric"
                value={form.mobile}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    mobile: e.target.value.replace(/\D/g, "").slice(0, 10),
                  }))
                }
                placeholder="9876543210"
                required
              />
              {errors.mobile && (
                <p className="text-xs text-red-600">{errors.mobile}</p>
              )}
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="fc-email">
                Email{" "}
              </Label>
              <Input
                id="fc-email"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="director@example.com"
              />
              {errors.email && (
                <p className="text-xs text-red-600">{errors.email}</p>
              )}
            </div>

            <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <Checkbox
                id="fc-consent"
                checked={consent}
                onCheckedChange={(v) => setConsent(v === true)}
                className="mt-0.5"
              />
              <Label
                htmlFor="fc-consent"
                className="text-xs leading-relaxed font-normal"
              >
                The director has authorised us to fetch and store their credit
                information report from CIBIL for this credit assessment.
              </Label>
            </div>
            {serverError && (
              <p className="text-xs text-red-600">{serverError}</p>
            )}

            <DialogFooter>
              <Button
                type="submit"
                className="w-full gap-2"
                disabled={!consent}
              >
                <ShieldCheck className="h-4 w-4" /> Fetch CIBIL
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
